import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  runTransaction,
  limit,
  startAfter
} from "firebase/firestore"
import { db, auth, uploadImageToStorage } from "@/lib/firebase"
import type { Message, MessageReplyTo, UserData } from "@/types/models"

import { getFlatImageUrlsFromMessage } from '@/lib/chatImageBurst'

// Safely convert Firestore Timestamp or other values to Date with fallback
function toDateSafe(value: any, fallback: Date): Date {
  // Explicitly handle null/undefined (important for mobile where serverTimestamp may not be resolved yet)
  if (value === null || value === undefined) return fallback
  if (value instanceof Timestamp) return value.toDate()
  if (value && typeof value.toDate === 'function') return value.toDate()
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value)
    if (!isNaN(d.getTime())) return d
  }
  return fallback
}

// Allow messages from user or artist (no more AI assistant)
export type SenderType = 'user' | 'artist'

/** Device uploads plus attached portfolio flashes/tattoos allowed together before send */
export const CHAT_COMBINED_ATTACHMENT_LIMIT = 4

// Simple user info for display in chats
export interface ChatUserInfo {
  uid: string
  email: string
  displayName?: string
  instagram?: string
  avatarUrl?: string
}

/**
 * Build a serializable reply snapshot for persisting on a new message.
 * Returns null for types that cannot be quoted (notifications, booking cards, etc.).
 */
export function buildMessageReplyToSnapshot(message: Message): MessageReplyTo | null {
  if (message.type !== 'text' && message.type !== 'image') return null
  if (message.type === 'text') {
    const trimmed = (message.content || '').trim()
    if (!trimmed) return null
    const previewText = trimmed.length > 240 ? `${trimmed.slice(0, 237)}...` : trimmed
    return {
      messageId: message.id,
      senderId: message.senderId,
      senderType: message.senderType,
      sourceMessageType: 'text',
      previewText,
    }
  }
  const caption = (message.content || '').trim()
  return {
    messageId: message.id,
    senderId: message.senderId,
    senderType: message.senderType,
    sourceMessageType: 'image',
    previewText: caption || 'Photo',
  }
}

/** Reply target for a contiguous bare-image burst (uses first message id as anchor). */
export function buildBurstImageReplySnapshot(messages: Message[]): MessageReplyTo | null {
  if (!messages.length) return null
  const anchorMessage = messages[0]
  let totalImages = 0
  for (const m of messages) {
    totalImages += getFlatImageUrlsFromMessage(m).length
  }
  if (totalImages === 0) return null
  const lastWithCaption = [...messages].reverse().find(m => (m.content || '').trim())
  const caption = (lastWithCaption?.content || '').trim()
  const previewText = totalImages > 1 ? `${totalImages} photos` : caption || 'Photo'
  return {
    messageId: anchorMessage.id,
    senderId: anchorMessage.senderId,
    senderType: anchorMessage.senderType,
    sourceMessageType: 'image',
    previewText,
  }
}

// Get the authenticated user ID
export function getUserId(): string {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User must be authenticated to perform this action')
  }
  return user.uid
}

// Check if the current user is an artist
export async function isCurrentUserArtist(): Promise<boolean> {
  try {
    const userId = getUserId()
    const userDoc = await getDoc(doc(db, 'users', userId))
    
    if (userDoc.exists()) {
      const userData = userDoc.data() as UserData
      return userData.isArtist === true
    }
    
    return false
  } catch (error) {
    console.error('Error checking if user is artist:', error)
    return false
  }
}

// Fetch chats for the current user (either as user or artist)
export async function fetchChatsForCurrentUser(): Promise<Chat[]> {
  try {
    const isArtist = await isCurrentUserArtist()
    
    if (isArtist) {
      return await fetchArtistChats()
    } else {
      return await fetchUserChats()
    }
  } catch (error) {
    console.error('Error fetching chats for current user:', error)
    return []
  }
}

// Fetch an existing chat by ID
export async function fetchChatById(chatId: string): Promise<Chat | null> {
  try {
    const chatDoc = await getDoc(doc(db, "chats", chatId))
    if (!chatDoc.exists()) return null

    const data = chatDoc.data()
    const createdAt = toDateSafe(data.createdAt, new Date())
    // On mobile, serverTimestamp may be null initially, so use client timestamp as fallback
    const lastMessageAt = toDateSafe(
      data.lastMessageAt || data._lastMessageAtClient, 
      data._lastMessageAtClient ? toDateSafe(data._lastMessageAtClient, createdAt) : createdAt
    )
    return {
      id: chatDoc.id,
      userId: data.userId,
      artistId: data.artistId,
      flashId: data.flashId || null,
      bookingId: data.bookingId, // Include bookingId from Firestore
      createdAt,
      lastMessageAt,
      lastMessage: data.lastMessage,
      status: data.status,
      messages: data.messages?.map((msg: any) => ({
        ...msg,
        timestamp: toDateSafe(msg.timestamp, createdAt),
        ...(msg.bookingData && {
          bookingData: {
            ...msg.bookingData,
            earliestDate: toDateSafe(msg.bookingData.earliestDate, createdAt)
          }
        })
      })) || []
    }
  } catch (error) {
    console.error("Error fetching chat:", error)
    return null
  }
}

/**
 * Set up a real-time listener for chat updates
 * @param chatId The chat ID to listen to
 * @param onUpdate Callback function called when chat data updates
 * @returns Unsubscribe function to stop listening
 */
export function listenToChatUpdates(
  chatId: string,
  onUpdate: (chat: Chat | null) => void
): () => void {
  const chatRef = doc(db, "chats", chatId)
  
  return onSnapshot(chatRef, (doc) => {
    if (!doc.exists()) {
      onUpdate(null)
      return
    }

    const data = doc.data()
    console.log('[listenToChatUpdates] Raw Firestore data:', {
      userId: data.userId,
      artistId: data.artistId,
      hasTypingFields: {
        userIsTyping: data.userIsTyping !== undefined,
        artistIsTyping: data.artistIsTyping !== undefined
      },
      userIsTyping: data.userIsTyping,
      artistIsTyping: data.artistIsTyping
    })
    
    const createdAt = toDateSafe(data.createdAt, new Date())
    const lastMessageAt = toDateSafe(data.lastMessageAt, createdAt)
    const chat: Chat = {
      id: doc.id,
      userId: data.userId,
      artistId: data.artistId,
      flashId: data.flashId || null,
      createdAt,
      lastMessageAt,
      lastMessage: data.lastMessage,
      status: data.status,
      messages: data.messages?.map((msg: any) => ({
        ...msg,
        timestamp: toDateSafe(msg.timestamp, createdAt),
        ...(msg.bookingData && {
          bookingData: {
            ...msg.bookingData,
            earliestDate: toDateSafe(msg.bookingData.earliestDate, createdAt)
          }
        }),
        ...(msg.simpleBookingData && {
          simpleBookingData: {
            ...msg.simpleBookingData,
            availableDates: msg.simpleBookingData.availableDates?.map((date: any) => toDateSafe(date, createdAt)) || []
          }
        })
      })) || [],
      userIsTyping: data.userIsTyping,
      artistIsTyping: data.artistIsTyping,
      userTypingTimestamp: data.userTypingTimestamp ? toDateSafe(data.userTypingTimestamp, new Date()) : undefined,
      artistTypingTimestamp: data.artistTypingTimestamp ? toDateSafe(data.artistTypingTimestamp, new Date()) : undefined
    }
    
    onUpdate(chat)
  }, (error) => {
    console.error("Error listening to chat updates:", error)
    onUpdate(null)
  })
}


/**
 * Add a new message to an existing chat
 * Uses transactions to prevent race conditions when adding messages
 */
export async function addMessageToChat(
  chatId: string,
  content: string,
  senderId: string,
  senderType: SenderType,
  type: Message['type'] = 'text',
  clientGeneratedId?: string,
  replyTo?: MessageReplyTo
): Promise<string> {
  try {
    getUserId() // Verify user is authenticated
    const chatRef = doc(db, 'chats', chatId)
    
    const now = new Date()
    const newMessage: Message = {
      id: generateMessageId(),
      content,
      senderId,
      senderType,
      timestamp: now,
      type,
      isRead: false, // New messages start as unread by recipient
      // Delivery tracking fields
      status: 'sent', // Message is sent when it reaches Firestore
      ...(clientGeneratedId && { clientGeneratedId }), // Only include if defined
      sentAt: now, // Mark when it reached Firestore
      sendAttempts: 1,
      // Pre-populate notification structure for messages that will trigger notifications
      // Only add for user/artist messages (not system/notification messages)
      ...(type === 'text' && (senderType === 'user' || senderType === 'artist') && {
        notification: {
          notificationId: generateNotificationId(senderId),
          status: 'pending' as const,
          deliveryAttempts: 0
        }
      }),
      ...(replyTo && { replyTo })
    } as Message
    
    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)
      
      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }
      
      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      
      // Check for duplicate clientGeneratedId to prevent double-posting
      if (clientGeneratedId) {
        const isDuplicate = messages.some((m: Message) => m.clientGeneratedId === clientGeneratedId)
        if (isDuplicate) {
          console.log('[addMessageToChat] Duplicate clientGeneratedId detected, skipping:', clientGeneratedId)
          return // Exit transaction without updating
        }
      }
      
      console.log('[addMessageToChat] Transaction: current messages count:', messages.length)
      const updatedMessages = [...messages, newMessage]
      console.log('[addMessageToChat] Transaction: new messages count:', updatedMessages.length)
      
      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: content,
        lastMessageAt: Timestamp.now()
      })
    }, {
      maxAttempts: 5 // Firestore default, but explicit for clarity
    })
    
    // Return the server-generated ID
    return newMessage.id
  } catch (error) {
    console.error('Error adding message to chat:', error)
    throw error
  }
}

/**
 * Delete a message from a chat by its message ID
 */
export async function deleteMessageFromChat(
  chatId: string,
  messageId: string
): Promise<void> {
  try {
    const userId = getUserId() // Verify user is authenticated
    const chatRef = doc(db, 'chats', chatId)
    
    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)
      
      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }
      
      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      
      // Find the message to verify ownership
      const messageToDelete = messages.find((m: Message) => m.id === messageId)
      if (!messageToDelete) {
        throw new Error('Message not found')
      }
      
      // Only allow users to delete their own messages
      if (messageToDelete.senderId !== userId) {
        throw new Error('You can only delete your own messages')
      }
      
      // Filter out the message
      const updatedMessages = messages.filter((m: Message) => m.id !== messageId)
      
      // Update last message if we deleted the most recent one
      const lastMessage = updatedMessages.length > 0 
        ? updatedMessages[updatedMessages.length - 1].content 
        : ''
      const lastMessageAt = updatedMessages.length > 0
        ? updatedMessages[updatedMessages.length - 1].timestamp
        : Timestamp.now()
      
      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage,
        lastMessageAt
      })
    })
    
    console.log('[deleteMessageFromChat] Message deleted successfully:', messageId)
  } catch (error) {
    console.error('Error deleting message from chat:', error)
    throw error
  }
}

/**
 * Create a simple chat for contact artist functionality (no leads, no summaries)
 */
export async function createSimpleChat(artistId: string, flashId?: string): Promise<string> {
  const userId = getUserId() // This will throw if user is not authenticated
  const chatRef = doc(collection(db, 'chats'))
  const chatId = chatRef.id

  const chatData: Omit<Chat, 'id'> = {
    userId,
    artistId,
    flashId: flashId || null,
    createdAt: new Date(),
    lastMessageAt: new Date(),
    lastMessage: '', // Empty since no initial message
    status: 'active',
    messages: [] // Start with empty messages array
  }

  try {
    // Use client Timestamp to avoid null flicker on mobile
    await setDoc(chatRef, {
      ...chatData,
      createdAt: Timestamp.now(),
      lastMessageAt: Timestamp.now()
    })

    return chatId
  } catch (error) {
    console.error('Error creating simple chat:', error)
    throw error
  }
}

/**
 * Add a welcome message from the artist to a newly created chat.
 * This message is added atomically with optional user messages to ensure seamless visual transition.
 * Uses artist's userId as senderId to properly attribute the message.
 */
export async function addWelcomeMessageToChat(
  chatId: string,
  welcomeContent: string,
  artistUserId: string,
  userMessagesToAdd?: Array<{
    content: string
    senderId: string
    senderType: SenderType
    isImageUrl?: boolean
    clientGeneratedId?: string
    metadata?: {
      flashId?: string
      minPrice?: string
      minSize?: string
      isFlash?: boolean
      eventId?: string
      eventExclusive?: boolean
    }
    replyTo?: MessageReplyTo
  }>
): Promise<void> {
  try {
    getUserId() // Verify user is authenticated
    const chatRef = doc(db, 'chats', chatId)

    // Create the welcome message with artist's userId
    const welcomeMessage: Message = {
      id: generateMessageId(),
      content: welcomeContent,
      senderId: artistUserId, // Use artist's userId here
      senderType: 'artist',
      timestamp: new Date(),
      type: 'text',
      isRead: false,
      // Pre-populate notification structure
      notification: {
        notificationId: generateNotificationId(artistUserId),
        status: 'pending' as const,
        deliveryAttempts: 0
      }
    }

    // Build the messages array starting with welcome message
    const messagesToAdd = [welcomeMessage]

    // Add any user messages that should follow the welcome message
    if (userMessagesToAdd && userMessagesToAdd.length > 0) {
      for (const userMsg of userMessagesToAdd) {
        const isImageUrl = userMsg.isImageUrl || (userMsg.content.startsWith('http://') || userMsg.content.startsWith('https://'))
        
        messagesToAdd.push({
          id: generateMessageId(),
          content: isImageUrl ? '' : userMsg.content, // Image messages have empty content
          senderId: userMsg.senderId,
          senderType: userMsg.senderType,
          timestamp: new Date(),
          type: isImageUrl ? 'image' : 'text',
          isRead: false,
          ...(isImageUrl && { imageUrl: userMsg.content }), // Add imageUrl for image messages
          ...(userMsg.metadata && { metadata: userMsg.metadata }), // Add metadata for portfolio items
          // Pre-populate notification structure for user messages
          ...(userMsg.senderType === 'user' || userMsg.senderType === 'artist' ? {
            notification: {
              notificationId: generateNotificationId(userMsg.senderId),
              status: 'pending' as const,
              deliveryAttempts: 0
            }
          } : {}),
          ...(userMsg.replyTo && { replyTo: userMsg.replyTo })
        })
      }
    }

    // Use transaction to ensure atomic operation
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const existingMessages = chatData.messages || []
      
      // Combine existing messages with new messages
      const updatedMessages = [...existingMessages, ...messagesToAdd]
      
      // Update lastMessage based on the last non-image message added
      let lastMessageContent = ''
      for (let i = messagesToAdd.length - 1; i >= 0; i--) {
        const msg = messagesToAdd[i]
        if (msg.type === 'text') {
          lastMessageContent = msg.content
          break
        } else if (msg.type === 'image') {
          lastMessageContent = '📷 Image'
          break
        }
      }
      
      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: lastMessageContent,
        lastMessageAt: Timestamp.now()
      })
    })
  } catch (error) {
    console.error('[addWelcomeMessageToChat] Error:', error)
    throw error
  }
}

/**
 * Add a message with optional image to an existing chat
 */
export async function addMessageWithImageToChat(
  chatId: string,
  content: string,
  senderId: string,
  senderType: SenderType,
  imageFile?: File
): Promise<void> {
  try {
    getUserId() // Verify user is authenticated
    const chatRef = doc(db, 'chats', chatId)
    
    let imageUrl: string | undefined
    
    // Upload image if provided - pass senderId for storage path
    if (imageFile) {
      imageUrl = await uploadImageToStorage(imageFile, 'chat-images', senderId)
    }
    
    const newMessage: Message = {
      id: generateMessageId(),
      content: content,
      senderId,
      senderType,
      timestamp: new Date(),
      type: imageUrl ? 'image' : 'text',
      isRead: false, // New messages start as unread by recipient
      ...(imageUrl && { imageUrl }), // Only include imageUrl if it exists
      // Pre-populate notification structure
      ...(senderType === 'user' || senderType === 'artist' ? {
        notification: {
          notificationId: generateNotificationId(senderId),
          status: 'pending' as const,
          deliveryAttempts: 0
        }
      } : {})
    }
    
    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)
      
      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }
      
      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      const updatedMessages = [...messages, newMessage]
      
      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: imageUrl ? '📷 Image' : content,
        lastMessageAt: Timestamp.now()
      })
    })
  } catch (error) {
    console.error('Error adding message to chat:', error)
    throw error
  }
}

/**
 * Add an image message using an existing URL (no upload)
 * Uses transactions to prevent race conditions when adding multiple images
 */
export async function addImageUrlMessageToChat(
  chatId: string,
  imageUrl: string,
  senderId: string,
  senderType: SenderType,
  content = '',
  clientGeneratedId?: string,
  metadata?: {
    flashId?: string
    minPrice?: string
    minSize?: string
    isFlash?: boolean
    eventId?: string
    eventExclusive?: boolean
  },
  replyTo?: MessageReplyTo
): Promise<string> {
  try {
    getUserId()
    const chatRef = doc(db, 'chats', chatId)

    const now = new Date()
    
    // Filter out undefined values from metadata to prevent Firestore errors
    const cleanMetadata = metadata && Object.keys(metadata).length > 0
      ? Object.fromEntries(
          Object.entries(metadata).filter(([_, value]) => value !== undefined)
        )
      : undefined
    
    const newMessage: Message = {
      id: generateMessageId(),
      content,
      senderId,
      senderType,
      timestamp: now,
      type: 'image',
      imageUrl,
      isRead: false,
      // Delivery tracking fields
      status: 'sent', // Message is sent when it reaches Firestore
      clientGeneratedId, // Store client ID for matching with optimistic updates
      sentAt: now, // Mark when it reached Firestore
      sendAttempts: 1,
      // Portfolio metadata (only include if not empty after filtering)
      ...(cleanMetadata ? { metadata: cleanMetadata } : {}),
      // Pre-populate notification structure for messages that will trigger notifications
      // Only add for user/artist image messages
      ...(senderType === 'user' || senderType === 'artist' ? {
        notification: {
          notificationId: generateNotificationId(senderId),
          status: 'pending' as const,
          deliveryAttempts: 0
        }
      } : {}),
      ...(replyTo && { replyTo })
    } as Message

    // Use transaction to ensure atomic read-modify-write
    // Firestore will automatically retry on contention with exponential backoff (up to 5 times)
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      
      // Check for duplicate clientGeneratedId to prevent double-posting
      if (clientGeneratedId) {
        const isDuplicate = messages.some((m: Message) => m.clientGeneratedId === clientGeneratedId)
        if (isDuplicate) {
          console.log('[addImageUrlMessageToChat] Duplicate clientGeneratedId detected, skipping:', clientGeneratedId)
          return // Exit transaction without updating
        }
      }
      
      console.log('[addImageUrlMessageToChat] Transaction: current messages count:', messages.length)
      const updatedMessages = [...messages, newMessage]
      console.log('[addImageUrlMessageToChat] Transaction: new messages count:', updatedMessages.length)

      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: '📷 Image',
        lastMessageAt: Timestamp.now()
      })
    }, {
      maxAttempts: 5 // Firestore default, but explicit for clarity
    })
    
    // Return the message ID so we can update it later with real URL
    return newMessage.id
  } catch (error) {
    console.error('Error adding image URL message to chat:', error)
    throw error
  }
}

/**
 * Update an image message with the uploaded image URL
 * Used when optimistically adding a blob URL, then replacing with real URL after upload
 */
export async function updateImageMessageUrl(
  chatId: string,
  messageId: string,
  newImageUrl: string
): Promise<void> {
  try {
    console.log('[updateImageMessageUrl] Starting update:', {
      chatId,
      messageId,
      newImageUrl: newImageUrl.substring(0, 100)
    })
    
    getUserId()
    const chatRef = doc(db, 'chats', chatId)

    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        console.error('[updateImageMessageUrl] Chat not found:', chatId)
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      console.log('[updateImageMessageUrl] Found chat with messages:', messages.length)

      // Find the message to update
      const messageToUpdate = messages.find((msg: any) => msg.id === messageId)
      if (!messageToUpdate) {
        console.error('[updateImageMessageUrl] Message not found:', {
          messageId,
          availableMessageIds: messages.map((m: any) => m.id)
        })
      } else {
        console.log('[updateImageMessageUrl] Found message to update:', {
          messageId,
          oldImageUrl: messageToUpdate.imageUrl?.substring(0, 100),
          newImageUrl: newImageUrl.substring(0, 100)
        })
      }

      // Find and update the message with the new URL
      const updatedMessages = messages.map((msg: any) => 
        msg.id === messageId 
          ? { ...msg, imageUrl: newImageUrl }
          : msg
      )

      // Update the chat document
      console.log('[updateImageMessageUrl] Updating Firestore document...')
      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessageAt: Timestamp.now()
      })
    })
    
    console.log('[updateImageMessageUrl] Successfully updated message URL in Firestore')
  } catch (error) {
    console.error('Error updating image message URL:', error)
    throw error
  }
}

/**
 * Toggle the like status of a message
 */
export async function toggleMessageLike(
  chatId: string,
  messageId: string
): Promise<void> {
  try {
    getUserId()
    const chatRef = doc(db, 'chats', chatId)

    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const messages = chatData.messages || []

      const updatedMessages = messages.map((msg: any) => 
        msg.id === messageId 
          ? { ...msg, isLiked: !msg.isLiked }
          : msg
      )

      transaction.update(chatRef, {
        messages: updatedMessages
      })
    })
  } catch (error) {
    console.error('Error toggling message like:', error)
    throw error
  }
}

/**
 * Add a combined message with text and multiple images
 */
export async function addCombinedMessageToChat(
  chatId: string,
  textContent: string,
  imageUrls: string[],
  senderId: string,
  senderType: SenderType
): Promise<void> {
  try {
    getUserId()
    const chatRef = doc(db, 'chats', chatId)

    const newMessage: Message = {
      id: generateMessageId(),
      content: textContent,
      senderId,
      senderType,
      timestamp: new Date(),
      type: 'image', // Use 'image' type since it contains images
      imageUrls, // Store multiple image URLs
      isRead: false,
      // Pre-populate notification structure for messages that will trigger notifications
      ...(senderType === 'user' || senderType === 'artist' ? {
        notification: {
          notificationId: generateNotificationId(senderId),
          status: 'pending' as const,
          deliveryAttempts: 0
        }
      } : {})
    }

    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      const updatedMessages = [...messages, newMessage]

      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: imageUrls.length > 1 ? `📷 ${imageUrls.length} Images` : '📷 Image',
        lastMessageAt: Timestamp.now()
      })
    })
  } catch (error) {
    console.error('Error adding combined message to chat:', error)
    throw error
  }
}

/**
 * Add a booking selection message with availability data
 */
export async function addBookingSelectionMessageToChat(
  chatId: string,
  estimatedTime: number | 'unknown',
  earliestDate: Date,
  senderId: string,
  senderType: SenderType,
  preparationDays?: number
): Promise<void> {
  try {
    getUserId()
    const chatRef = doc(db, 'chats', chatId)

    const newMessage: Message = {
      id: generateMessageId(),
      content: 'Booking availability 📅',
      senderId,
      senderType,
      timestamp: new Date(),
      type: 'booking_selection',
      isRead: false,
      bookingData: {
        estimatedTime,
        earliestDate,
        ...(preparationDays !== undefined && { preparationDays })
      }
    }

    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      const updatedMessages = [...messages, newMessage]

      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: '📅 Booking availability',
        lastMessageAt: Timestamp.now()
      })
    })
  } catch (error) {
    console.error('Error adding booking selection message to chat:', error)
    throw error
  }
}

/**
 * Add a simple availability message to chat (for non-booking system artists)
 * This is a lightweight version for artists without the full booking system
 */
export async function addSimpleAvailabilityToChat(
  chatId: string,
  priceRangeMin: number,
  priceRangeMax: number,
  availableDates: Date[],
  estimatedTime: number | 'unknown' | undefined,
  senderId: string,
  senderType: SenderType,
  availableTimeSlots?: Record<string, string[]>
): Promise<void> {
  try {
    getUserId()
    const chatRef = doc(db, 'chats', chatId)

    const newMessage: Message = {
      id: generateMessageId(),
      content: 'Book Time',
      senderId,
      senderType,
      timestamp: new Date(),
      type: 'simple_availability',
      isRead: false,
      simpleBookingData: {
        priceRangeMin,
        priceRangeMax,
        availableDates,
        estimatedTime,
        availableTimeSlots
      }
    }

    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      const updatedMessages = [...messages, newMessage]

      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: 'Book Time',
        lastMessageAt: Timestamp.now()
      })
    })
  } catch (error) {
    console.error('Error adding simple booking offer message to chat:', error)
    throw error
  }
}

/**
 * Add a booking confirmation message to the chat
 * This shows that a booking has been successfully created
 */
export async function addBookingConfirmationMessageToChat(
  chatId: string,
  bookingDate: Date,
  priceRangeMin: number,
  priceRangeMax: number,
  estimatedTime: number | 'unknown',
  senderId: string,
  senderType: SenderType,
  bookingTime?: string
): Promise<string> {
  try {
    getUserId()
    const chatRef = doc(db, 'chats', chatId)

    const messageId = generateMessageId()
    const newMessage: Message = {
      id: messageId,
      content: 'Booking Confirmed',
      senderId,
      senderType,
      timestamp: new Date(),
      type: 'simple_availability',
      isRead: false,
      simpleBookingData: {
        priceRangeMin,
        priceRangeMax,
        availableDates: [bookingDate],
        estimatedTime, // Add the time for confirmation display
        bookingTime, // Include the booking time for display
        isConfirmed: true
      }
    }

    // Use transaction to ensure atomic read-modify-write
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)

      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }

      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      const updatedMessages = [...messages, newMessage]

      transaction.update(chatRef, {
        messages: updatedMessages,
        lastMessage: 'Booking Confirmed',
        lastMessageAt: Timestamp.now()
      })
    })
    
    return messageId
  } catch (error) {
    console.error('Error adding booking confirmation message to chat:', error)
    throw error
  }
}

/**
 * Fetch all chats for the authenticated user
 */
export async function fetchUserChats(): Promise<Chat[]> {
  try {
    const userId = getUserId()
    
    const chatsQuery = query(
      collection(db, 'chats'),
      where('userId', '==', userId),
      orderBy('lastMessageAt', 'desc')
    )
    
    const querySnapshot = await getDocs(chatsQuery)
    
    const chats: Chat[] = querySnapshot.docs.map(doc => {
      const data = doc.data()
      const createdAt = toDateSafe(data.createdAt, new Date())
      // On mobile, serverTimestamp may be null initially, so use client timestamp as fallback
      const lastMessageAt = toDateSafe(
        data.lastMessageAt || data._lastMessageAtClient, 
        data._lastMessageAtClient ? toDateSafe(data._lastMessageAtClient, createdAt) : createdAt
      )
      return {
        id: doc.id,
        userId: data.userId,
        artistId: data.artistId,
        flashId: data.flashId || null,
        bookingId: data.bookingId, // Include bookingId from Firestore
        createdAt,
        lastMessageAt,
        lastMessage: data.lastMessage,
        status: data.status,
        messages: data.messages?.map((msg: any) => ({
          ...msg,
          timestamp: toDateSafe(msg.timestamp, createdAt),
          isRead: msg.isRead || false, // Default to false if not set
          ...(msg.bookingData && ({
            bookingData: {
              ...msg.bookingData,
              earliestDate: toDateSafe(msg.bookingData.earliestDate, createdAt)
            }
          }))
        })) || []
      }
    })
    
    return chats
  } catch (error) {
    console.error('Error fetching user chats:', error)
    return []
  }
}

/**
 * Fetch all chats for the authenticated artist
 */
export async function fetchArtistChats(): Promise<Chat[]> {
  try {
    const userId = getUserId()
    
    // Get the current user's artist document ID from their user document
    const userDoc = await getDoc(doc(db, 'users', userId))
    if (!userDoc.exists() || !userDoc.data().isArtist || !userDoc.data().artistId) {
      console.log('[fetchArtistChats] User is not an artist or missing artistId')
      return []
    }
    
    const artistId = userDoc.data().artistId
    console.log('[fetchArtistChats] Fetching chats for artistId:', artistId)
    
    const chatsQuery = query(
      collection(db, 'chats'),
      where('artistId', '==', artistId),
      orderBy('lastMessageAt', 'desc')
    )
    
    const querySnapshot = await getDocs(chatsQuery)
    console.log('[fetchArtistChats] Found', querySnapshot.docs.length, 'chats')
    
    const chats: Chat[] = querySnapshot.docs.map(doc => {
      const data = doc.data()
      const createdAt = toDateSafe(data.createdAt, new Date())
      // On mobile, serverTimestamp may be null initially, so use client timestamp as fallback
      const lastMessageAt = toDateSafe(
        data.lastMessageAt || data._lastMessageAtClient, 
        data._lastMessageAtClient ? toDateSafe(data._lastMessageAtClient, createdAt) : createdAt
      )
      return {
        id: doc.id,
        userId: data.userId,
        artistId: data.artistId,
        flashId: data.flashId || null,
        bookingId: data.bookingId, // Include bookingId from Firestore
        createdAt,
        lastMessageAt,
        lastMessage: data.lastMessage,
        status: data.status,
        messages: data.messages?.map((msg: any) => ({
          ...msg,
          timestamp: toDateSafe(msg.timestamp, createdAt),
          isRead: msg.isRead || false, // Default to false if not set
          ...(msg.bookingData && {
            bookingData: {
              ...msg.bookingData,
              earliestDate: toDateSafe(msg.bookingData.earliestDate, createdAt)
            }
          })
        })) || []
      }
    })
    
    return chats
  } catch (error) {
    console.error('Error fetching artist chats:', error)
    return []
  }
}

/**
 * Subscribe to all chats for the authenticated user (real-time)
 */
export function subscribeToUserChats(onUpdate: (chats: Chat[]) => void): () => void {
  const userId = getUserId()
  
  const chatsQuery = query(
    collection(db, 'chats'),
    where('userId', '==', userId),
    orderBy('lastMessageAt', 'desc')
  )
  
  return onSnapshot(chatsQuery, (querySnapshot) => {
    console.log('[subscribeToUserChats] Chat update received, count:', querySnapshot.docs.length)
    
    const chats: Chat[] = querySnapshot.docs.map(doc => {
      const data = doc.data()
      const createdAt = toDateSafe(data.createdAt, new Date())
      const lastMessageAt = toDateSafe(
        data.lastMessageAt || data._lastMessageAtClient, 
        data._lastMessageAtClient ? toDateSafe(data._lastMessageAtClient, createdAt) : createdAt
      )
      return {
        id: doc.id,
        userId: data.userId,
        artistId: data.artistId,
        flashId: data.flashId || null,
        bookingId: data.bookingId,
        createdAt,
        lastMessageAt,
        lastMessage: data.lastMessage,
        status: data.status,
        messages: data.messages?.map((msg: any) => ({
          ...msg,
          timestamp: toDateSafe(msg.timestamp, createdAt),
          isRead: msg.isRead || false,
          ...(msg.bookingData && ({
            bookingData: {
              ...msg.bookingData,
              earliestDate: toDateSafe(msg.bookingData.earliestDate, createdAt)
            }
          }))
        })) || []
      }
    })
    
    onUpdate(chats)
  }, (error) => {
    console.error('[subscribeToUserChats] Error in chat subscription:', error)
    onUpdate([])
  })
}

/**
 * Subscribe to all chats for the authenticated artist (real-time)
 */
export async function subscribeToArtistChats(onUpdate: (chats: Chat[]) => void): Promise<() => void> {
  const userId = getUserId()
  
  // First, get the artist ID from the user document
  const userDoc = await getDoc(doc(db, 'users', userId))
  if (!userDoc.exists() || !userDoc.data().isArtist || !userDoc.data().artistId) {
    console.log('[subscribeToArtistChats] User is not an artist or missing artistId')
    onUpdate([])
    return () => {}
  }
  
  const artistId = userDoc.data().artistId
  console.log('[subscribeToArtistChats] Subscribing to chats for artistId:', artistId)
  
  const chatsQuery = query(
    collection(db, 'chats'),
    where('artistId', '==', artistId),
    orderBy('lastMessageAt', 'desc')
  )
  
  return onSnapshot(chatsQuery, (querySnapshot) => {
    console.log('[subscribeToArtistChats] Chat update received, count:', querySnapshot.docs.length)
    
    const chats: Chat[] = querySnapshot.docs.map(doc => {
      const data = doc.data()
      const createdAt = toDateSafe(data.createdAt, new Date())
      const lastMessageAt = toDateSafe(
        data.lastMessageAt || data._lastMessageAtClient, 
        data._lastMessageAtClient ? toDateSafe(data._lastMessageAtClient, createdAt) : createdAt
      )
      return {
        id: doc.id,
        userId: data.userId,
        artistId: data.artistId,
        flashId: data.flashId || null,
        bookingId: data.bookingId,
        createdAt,
        lastMessageAt,
        lastMessage: data.lastMessage,
        status: data.status,
        messages: data.messages?.map((msg: any) => ({
          ...msg,
          timestamp: toDateSafe(msg.timestamp, createdAt),
          isRead: msg.isRead || false,
          ...(msg.bookingData && {
            bookingData: {
              ...msg.bookingData,
              earliestDate: toDateSafe(msg.bookingData.earliestDate, createdAt)
            }
          })
        })) || []
      }
    })
    
    onUpdate(chats)
  }, (error) => {
    console.error('[subscribeToArtistChats] Error in chat subscription:', error)
    onUpdate([])
  })
}

/**
 * Find an existing chat between a user and an artist
 * @param userId The user ID
 * @param artistId The artist ID
 * @returns The existing chat ID if found, null otherwise
 */
export async function findExistingChat(userId: string, artistId: string): Promise<string | null> {
  try {
    const chatsQuery = query(
      collection(db, 'chats'),
      where('userId', '==', userId),
      where('artistId', '==', artistId)
    )
    
    const querySnapshot = await getDocs(chatsQuery)
    
    if (querySnapshot.docs.length > 0) {
      // Return the most recent chat if multiple exist (shouldn't happen, but handle it)
      const chats = querySnapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          lastMessageAt: toDateSafe(data.lastMessageAt || data._lastMessageAtClient, data.createdAt ? toDateSafe(data.createdAt, new Date()) : new Date())
        }
      })
      
      // Sort by lastMessageAt descending and return the most recent
      chats.sort((a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime())
      return chats[0].id
    }
    
    return null
  } catch (error) {
    console.error('Error finding existing chat:', error)
    return null
  }
}

/**
 * Get unread message count for a chat
 */
export function getUnreadCount(chat: Chat, currentUserId: string): number {
  return chat.messages.filter(message => 
    message.senderType === 'artist' && // Only count messages from artist
    message.senderId !== currentUserId && // Not sent by current user
    !message.isRead // Not marked as read
  ).length
}

/**
 * Get unread message count for a chat from artist perspective
 */
export function getArtistUnreadCount(chat: Chat, currentUserId: string): number {
  return chat.messages.filter(message => 
    message.senderType === 'user' && // Only count messages from user
    message.senderId !== currentUserId && // Not sent by current user (artist)
    !message.isRead // Not marked as read
  ).length
}

/**
 * Get unread message count for current user (artist or regular user)
 */
export async function getUnreadCountForCurrentUser(chat: Chat, currentUserId: string): Promise<number> {
  try {
    const isArtist = await isCurrentUserArtist()
    
    if (isArtist) {
      return getArtistUnreadCount(chat, currentUserId)
    } else {
      return getUnreadCount(chat, currentUserId)
    }
  } catch (error) {
    console.error('Error getting unread count for current user:', error)
    return 0
  }
}

/**
 * Mark messages as read in a chat
 * Uses reduced retry attempts to avoid transaction conflicts with message sending
 */
export async function markMessagesAsRead(chatId: string): Promise<void> {
  try {
    const userId = getUserId()
    const chatRef = doc(db, 'chats', chatId)
    
    // Use transaction with reduced retries to avoid competing with message sends
    // If this fails due to contention, it's okay - next read will mark messages as read
    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(chatRef)
      
      if (!chatDoc.exists()) {
        throw new Error('Chat not found')
      }
      
      const chatData = chatDoc.data()
      const messages = chatData.messages || []
      console.log('[markMessagesAsRead] Transaction: current messages count:', messages.length)
      
      // Mark all unread messages from others as read
      // Exclude notification messages as they are system messages and don't need to be marked as read
      const updatedMessages = messages.map((message: any) => {
        if (message.senderId !== userId && !message.isRead && message.type !== 'notification') {
          return {
            ...message,
            isRead: true
          }
        }
        return message
      })
      
      // Only update if there were changes
      const hasChanges = updatedMessages.some((msg: any, idx: number) => 
        msg.isRead !== messages[idx].isRead
      )
      
      if (hasChanges) {
        console.log('[markMessagesAsRead] Transaction: marking messages as read, count still:', updatedMessages.length)
        transaction.update(chatRef, {
          messages: updatedMessages
        })
      } else {
        console.log('[markMessagesAsRead] Transaction: no changes needed')
      }
    }, {
      maxAttempts: 2 // Reduced retries - marking as read is not critical
    })
  } catch (error) {
    // Don't throw on mark as read failures - it's not critical
    // The next time messages are loaded, they'll be marked as read
    console.warn('Failed to mark messages as read (non-critical):', error)
  }
}

/**
 * Set typing indicator for current user in a chat
 */
export async function setTypingIndicator(
  chatId: string, 
  isArtist: boolean
): Promise<void> {
  try {
    getUserId() // Verify user is authenticated
    const chatRef = doc(db, 'chats', chatId)
    
    const fieldName = isArtist ? 'artistIsTyping' : 'userIsTyping'
    const timestampField = isArtist ? 'artistTypingTimestamp' : 'userTypingTimestamp'
    
    // Use updateDoc to only update typing fields without touching messages
    await updateDoc(chatRef, {
      [fieldName]: true,
      [timestampField]: Timestamp.now()
    })
  } catch (error) {
    console.error('[setTypingIndicator] Error:', error)
    // Don't throw - typing indicators are not critical
  }
}

/**
 * Clear typing indicator for current user in a chat
 */
export async function clearTypingIndicator(
  chatId: string,
  isArtist: boolean
): Promise<void> {
  try {
    getUserId() // Verify user is authenticated
    const chatRef = doc(db, 'chats', chatId)
    
    const fieldName = isArtist ? 'artistIsTyping' : 'userIsTyping'
    const timestampField = isArtist ? 'artistTypingTimestamp' : 'userTypingTimestamp'
    
    // Use updateDoc to only update typing fields without touching messages
    await updateDoc(chatRef, {
      [fieldName]: false,
      [timestampField]: Timestamp.now()
    })
  } catch (error) {
    console.error('[clearTypingIndicator] Error:', error)
    // Don't throw - typing indicators are not critical
  }
}

const CHAT_LIST_PREVIEW_MAX_CHARS = 40

/** Middle dot with thin spaces — "message · 2d" */
const CHAT_LIST_PREVIEW_SEP = '\u2009\u00B7\u2009'

/**
 * Short relative age for chat list suffix (e.g. 20m, 20h, 2d, 1w).
 */
export function formatChatListAgeSuffix(at: Date): string {
  const ts = at.getTime()
  if (Number.isNaN(ts)) return 'now'
  const diff = Date.now() - ts
  if (diff < 60_000) return 'now'
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 56) return `${weeks}w`
  const months = Math.floor(days / 30)
  return `${Math.max(1, months)}mo`
}

function truncateChatListPreviewText(text: string, max = CHAT_LIST_PREVIEW_MAX_CHARS): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1).trimEnd()}...`
}

function messageTimestampForList(m: Message): Date {
  const t = m.timestamp
  return t instanceof Date && !Number.isNaN(t.getTime()) ? t : new Date()
}

function peerListPreviewBody(last: Message): string {
  if (last.type === 'image') {
    const urls = getFlatImageUrlsFromMessage(last)
    if (urls.length > 1) return `📷 ${urls.length} Images`
    return '📷 Image'
  }
  if (last.type === 'booking_selection' || last.type === 'past_booking') {
    return (last.content || '').trim() || 'Booking'
  }
  if (last.type === 'simple_availability') {
    return (last.content || '').trim() || 'Availability'
  }
  if (last.type === 'notification') {
    return (last.content || '').trim() || 'Update'
  }
  return (last.content || '').trim() || 'Message'
}

/**
 * Chat list row: own last message → receipt only; peer last message → preview + age suffix.
 */
export type ChatListMessageReceipt =
  | 'sent'
  | 'seen'
  | 'availability_sent'
  | 'availability_seen'
  | null

export function buildChatListPreviewFields(
  chat: Chat,
  currentUserId: string,
): { lastMessagePreview: string; messageReceipt: ChatListMessageReceipt } {
  if (!chat.messages?.length) {
    return { lastMessagePreview: '', messageReceipt: null }
  }

  const last = chat.messages[chat.messages.length - 1]
  const fromSelf = last.senderId === currentUserId

  if (fromSelf) {
    const seen =
      last.isRead === true ||
      last.status === 'read' ||
      !!(last.readAt && last.readAt instanceof Date && !Number.isNaN(last.readAt.getTime()))
    const isAvailabilityMessage =
      last.type === 'booking_selection' || last.type === 'simple_availability'
    return {
      lastMessagePreview: '',
      messageReceipt: isAvailabilityMessage
        ? seen
          ? 'availability_seen'
          : 'availability_sent'
        : seen
          ? 'seen'
          : 'sent',
    }
  }

  const text = truncateChatListPreviewText(peerListPreviewBody(last))
  const suffix = formatChatListAgeSuffix(messageTimestampForList(last))
  return {
    lastMessagePreview: `${text}${CHAT_LIST_PREVIEW_SEP}${suffix}`,
    messageReceipt: null,
  }
}

/**
 * Fetch user info by user ID for chat display
 */
export async function fetchUserById(userId: string): Promise<ChatUserInfo | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId))
    if (!userDoc.exists()) return null

    const data = userDoc.data() as UserData
    return {
      uid: userId,
      email: data.email,
      displayName: data.displayName,
      instagram: data.instagram,
      avatarUrl: data.avatarUrl
    }
  } catch (error) {
    console.error('Error fetching user by ID:', error)
    return null
  }
}

/**
 * Fetch multiple users by their IDs
 */
export async function fetchUsersByIds(userIds: string[]): Promise<ChatUserInfo[]> {
  const uniqueIds = Array.from(new Set(userIds))
  const users = await Promise.all(uniqueIds.map(id => fetchUserById(id)))
  return users.filter((user): user is ChatUserInfo => user !== null)
}

// Helper functions
function generateMessageId(): string {
  return 'msg_' + Math.random().toString(36).substring(2, 15)
}

/**
 * Generate a notification ID in the format: notif_timestamp_userId_hash
 * This matches the backend's expected format for notification tracking
 */
function generateNotificationId(userId: string): string {
  const timestamp = Date.now()
  const randomHash = Math.random().toString(36).substring(2, 15)
  return `notif_${timestamp}_${userId}_${randomHash}`
} 

export async function trackChatOpened(
  chatId: string,
  artistId: string,
  source: string
): Promise<void> {
  try {
    const userId = getUserId()
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)

    if (userDoc.exists()) {
      const userData = userDoc.data()
      const openedChats = userData.openedChats || []

      // Add new chat entry
      openedChats.push({
        chatId,
        artistId,
        source,
        timestamp: new Date()
      })

      // Update user document
      await setDoc(userRef, {
        ...userData,
        openedChats
      }, { merge: true })
    }
  } catch (error) {
    console.error('Error tracking chat opened:', error)
  }
}

/**
 * OPTIMIZATION: Fetch only lightweight chat metadata (excluding full message arrays)
 * This is used for chat list views where full message history is not needed
 */
function mapChatMetadata(doc: any): Omit<Chat, 'messages'> & { messages: Message[] } {
  const data = doc.data()
  const createdAt = toDateSafe(data.createdAt, new Date())
  const lastMessageAt = toDateSafe(
    data.lastMessageAt || data._lastMessageAtClient,
    data._lastMessageAtClient ? toDateSafe(data._lastMessageAtClient, createdAt) : createdAt
  )
  
  // OPTIMIZATION: Only map messages array if it exists (don't load full content for list view)
  // For list view, we only need basic message metadata
  const messages = data.messages?.map((msg: any) => ({
    ...msg,
    timestamp: toDateSafe(msg.timestamp, createdAt),
    isRead: msg.isRead || false
  })) || []
  
  return {
    id: doc.id,
    userId: data.userId,
    artistId: data.artistId,
    flashId: data.flashId || null,
    bookingId: data.bookingId,
    createdAt,
    lastMessageAt,
    lastMessage: data.lastMessage,
    status: data.status,
    messages,
    userIsTyping: data.userIsTyping,
    artistIsTyping: data.artistIsTyping,
    userTypingTimestamp: data.userTypingTimestamp ? toDateSafe(data.userTypingTimestamp, new Date()) : undefined,
    artistTypingTimestamp: data.artistTypingTimestamp ? toDateSafe(data.artistTypingTimestamp, new Date()) : undefined
  }
}

/**
 * OPTIMIZATION: Subscribe with automatic pagination
 * Loads initial batch of chats, then loads more as needed
 */
export function subscribeToUserChatsWithPagination(
  onUpdate: (chats: Chat[], hasMore: boolean) => void,
  pageSize: number = 30
): () => void {
  const userId = getUserId()
  let allChats: Chat[] = []

  const chatsQuery = query(
    collection(db, 'chats'),
    where('userId', '==', userId),
    orderBy('lastMessageAt', 'desc'),
    limit(pageSize)
  )

  const unsubscribe = onSnapshot(chatsQuery, (querySnapshot) => {
    console.log('[subscribeToUserChatsWithPagination] Initial batch received, count:', querySnapshot.docs.length)
    
    allChats = querySnapshot.docs.map(mapChatMetadata)
    
    const hasMore = querySnapshot.docs.length === pageSize
    onUpdate(allChats, hasMore)
  }, (error) => {
    console.error('[subscribeToUserChatsWithPagination] Error in chat subscription:', error)
    onUpdate([], false)
  })

  return unsubscribe
}

/**
 * OPTIMIZATION: Load more chats for pagination
 */
export async function loadMoreUserChats(
  lastVisibleChat: any,
  pageSize: number = 30
): Promise<Chat[]> {
  try {
    const userId = getUserId()
    
    const nextQuery = query(
      collection(db, 'chats'),
      where('userId', '==', userId),
      orderBy('lastMessageAt', 'desc'),
      startAfter(lastVisibleChat),
      limit(pageSize)
    )
    
    const querySnapshot = await getDocs(nextQuery)
    const chats = querySnapshot.docs.map(mapChatMetadata)
    
    console.log('[loadMoreUserChats] Loaded', chats.length, 'more chats')
    return chats
  } catch (error) {
    console.error('[loadMoreUserChats] Error loading more chats:', error)
    return []
  }
}

/**
 * OPTIMIZATION: Subscribe with automatic pagination for artists
 */
export async function subscribeToArtistChatsWithPagination(
  onUpdate: (chats: Chat[], hasMore: boolean) => void,
  pageSize: number = 30
): Promise<() => void> {
  const userId = getUserId()

  // First, get the artist ID from the user document
  const userDoc = await getDoc(doc(db, 'users', userId))
  if (!userDoc.exists() || !userDoc.data().isArtist || !userDoc.data().artistId) {
    console.log('[subscribeToArtistChatsWithPagination] User is not an artist or missing artistId')
    onUpdate([], false)
    return () => {}
  }

  const artistId = userDoc.data().artistId
  console.log('[subscribeToArtistChatsWithPagination] Subscribing to paginated chats for artistId:', artistId)

  const chatsQuery = query(
    collection(db, 'chats'),
    where('artistId', '==', artistId),
    orderBy('lastMessageAt', 'desc'),
    limit(pageSize)
  )

  return onSnapshot(chatsQuery, (querySnapshot) => {
    console.log('[subscribeToArtistChatsWithPagination] Initial batch received, count:', querySnapshot.docs.length)
    
    const chats = querySnapshot.docs.map(mapChatMetadata)
    const hasMore = querySnapshot.docs.length === pageSize
    onUpdate(chats, hasMore)
  }, (error) => {
    console.error('[subscribeToArtistChatsWithPagination] Error in chat subscription:', error)
    onUpdate([], false)
  })
}

/**
 * OPTIMIZATION: Load more chats for pagination (artist view)
 */
export async function loadMoreArtistChats(
  artistId: string,
  lastVisibleChat: any,
  pageSize: number = 30
): Promise<Chat[]> {
  try {
    const nextQuery = query(
      collection(db, 'chats'),
      where('artistId', '==', artistId),
      orderBy('lastMessageAt', 'desc'),
      startAfter(lastVisibleChat),
      limit(pageSize)
    )
    
    const querySnapshot = await getDocs(nextQuery)
    const chats = querySnapshot.docs.map(mapChatMetadata)
    
    console.log('[loadMoreArtistChats] Loaded', chats.length, 'more chats')
    return chats
  } catch (error) {
    console.error('[loadMoreArtistChats] Error loading more chats:', error)
    return []
  }
} 

export interface Chat {
  id: string
  userId: string
  artistId: string
  flashId: string | null
  bookingId?: string // ID of associated booking if this chat has a confirmed booking
  createdAt: Date
  lastMessageAt: Date
  lastMessage: string
  status: 'booked' | 'artist_message_needed' | 'waiting_for_client' | 'active' | 'archived'
  messages: Message[]
  userIsTyping?: boolean
  artistIsTyping?: boolean
  userTypingTimestamp?: Date
  artistTypingTimestamp?: Date
}
